// GUSA-10847
/* eslint-disable max-lines */
import {createSelector} from 'reselect';
import {find, propEq, flatten, compose, pluck} from 'ramda';

import type {CategoryAppliedFiltersType} from 'constants/flow/flowTypesCategory';
import type {
    ApplicationStoreType,
    FilterDataType,
    FilterItemType,
    ActiveSortType,
    CategoryType,
} from 'constants/flow/flowTypes';
import type {ProductType} from 'constants/flow/flowTypesProduct';
import {
    NEUTRAL_CATEGORY_ID,
    PRODUCT_BADGE_CODE_FILTER,
    CLEARANCE_PRODUCT_BADGE_FILTER,
} from 'constants/category';

import type {CategoryItemsCountType} from 'reducers/category/categoryItemsCount/categoryItemsCountType';
import type {
    FetchCategoryItemsResponseType,
    CategoryProductItemType,
} from 'reducers/category/categoryItemsType';
import {getFilters} from 'selectors/filters/filters';
import {isSearchPage} from 'selectors/router/router';
import {isProperty} from 'libs/utils/utils';

/**
 * Get window items
 * @param {{}} store - state
 * @returns {{}} state Return items
 */
export const getCategoryItems = (store: ApplicationStoreType): FetchCategoryItemsResponseType =>
    store.categoryItems.items;

/**
 * Get window items id
 * @param {ApplicationStoreType} store - state
 * @returns {number} - items id
 */
export const getCategoryItemsId = (store: ApplicationStoreType): number => store.categoryItems.id;

/**
 * Is category items available?
 * @param {ApplicationStoreType} store - state
 * @returns {boolean} flag "is category empty"
 */
export const isCategoryEmpty = createSelector(
    getCategoryItems,
    (items: FetchCategoryItemsResponseType): boolean => items.length === 0,
);

/**
 * Return current category items count
 * @param {ApplicationStoreType} store state
 * @returns {CategoryItemsCountType} filters
 */
export const getCategoryItemsCount = (store: ApplicationStoreType): CategoryItemsCountType =>
    store.categoryItemsCount;

/**
 * Check is status of categoryItemsCount is initial
 * @param {ApplicationStoreType} store state
 * @returns {boolean} is loaded
 */
export const isCategoryItemsCountInitial = createSelector(
    getCategoryItemsCount,
    (categoryItemsCount: CategoryItemsCountType): boolean => {
        return categoryItemsCount.status === 'initial';
    },
);

/**
 * Get category items count is fulfilled.
 * @param {ApplicationStoreType} store state
 * @returns {boolean} is loaded
 */
export const getCategoryItemsCountIsFulfilled = (store: ApplicationStoreType): boolean =>
    store.categoryItemsCount.status === 'fulfilled';

/**
 * Return current counts of filters
 * @param {ApplicationStoreType} store state
 * @returns {{[string]: FilterDataType}} counts for filters
 */
export const getCountFilters = createSelector(
    getCategoryItemsCount,
    (categoryItemsCount: CategoryItemsCountType): {[string]: FilterDataType} => {
        const newFilters = {};

        categoryItemsCount.filters.map(
            (filter: FilterDataType) => (newFilters[filter.code] = filter),
        );

        return newFilters;
    },
);

/**
 * Get current simple id
 * @param {{}} state - state
 * @param {{}} props - props
 * @returns {number} - id
 */
export const getCurrentId = (state, props) => props.itemId;

/**
 * Get children product by id
 * @param {ApplicationStoreType} store state
 * @param {number | string} id - product id to search
 * @returns {CategoryProductItemType} children product
 */
export const getWindowProduct = createSelector(
    getCategoryItems,
    (items: FetchCategoryItemsResponseType, id: number): {} => id,
    (items: FetchCategoryItemsResponseType, id): CategoryProductItemType =>
        compose(find(propEq('id', id)), flatten, pluck('children'))(items),
);

/**
 * Get color simples from window product
 */
export const getSimples = createSelector(
    getCategoryItems,
    getCurrentId,
    (items: FetchCategoryItemsResponseType, id: number): Array<CategoryProductItemType> => {
        const windowProduct = items.find(item => item.id === id);
        return windowProduct ? windowProduct.children : [];
    },
);

/**
 * Get colors for simple
 */
export const getColorsSizesProduct = createSelector(getSimples, (products: ProductType): Array<{
    id: string,
    color: string,
    image: string,
    sizes: Array<void>,
    color_value: string,
}> => {
    const colorList = [];

    products.map((item: {}): {} => {
        const cur = item;
        colorList.push({
            id: cur.id,
            color: cur.color,
            image: cur.color_image,
            sizes: [],
            color_value: cur.color_value,
        });

        return true;
    });

    return colorList;
});

/**
 * Returns total applied category filters count
 * @param {CategoryAppliedFiltersType} appliedFilters - Data about applied filters
 * @returns {number} - Count of applied filters
 */
export const getAppliedFiltersCount = (appliedFilters: CategoryAppliedFiltersType): number =>
    Object.keys(appliedFilters).reduce(
        (acc: number, key: string): number => acc + appliedFilters[key].length,
        0,
    );

/**
 * Return counters for eyeWearFilter
 * @param {ApplicationStoreType} store state
 * @returns {{eyeglasses: count | undefined, sunglasses: count | undefined}} - eyeWearFilter data
 */
export const getEyeWearFiltersCount = createSelector(getCountFilters, filters => {
    const eyeWearFiltersCounts = {};

    eyeWearFiltersCounts.eyeglasses = filters?.eyewear_type?.items.find(
        el => el.value === 'eyeglasses',
    )?.count;
    eyeWearFiltersCounts.sunglasses = filters?.eyewear_type?.items.find(
        el => el.value === 'sunglasses',
    )?.count;

    return eyeWearFiltersCounts;
});

/**
 * Prepare items for available filters
 * @param {FilterItemType} item current filter
 * @param {FilterItemType} countItem current count for filter
 * @returns {Array<FilterItemType>} - scope of available filters
 */
export const prepareItemsForFilter = (
    item: FilterItemType,
    countItem: FilterItemType,
): Array<FilterItemType> =>
    item.items.reduce(
        (scope: Array<FilterItemType>, oneItem: FilterDataType): Array<FilterDataType> => {
            if (!countItem) return scope.concat(oneItem);
            if (countItem.items.some(cFilter => oneItem.value_id === cFilter.value_id)) {
                return scope.concat(oneItem);
            }
            return scope;
        },
        [],
    );

/**
 * It returns available filters for current category
 * @param {Array<FilterDataType>} filters for current category
 * @param {Array<FilterDataType>} countFilters counts for filters for current category
 * @returns {Array<FilterDataType>} - available filters
 */
export const getAvailableFilters = createSelector(
    getFilters,
    getCountFilters,
    (
        filters: Array<FilterDataType>,
        countFilters: Array<FilterDataType>,
    ): Array<FilterDataType> => ({
        ...filters,
        items: filters.items.reduce(
            (newFilters: Array<FilterDataType>, item: FilterDataType): Array<FilterDataType> => {
                if (countFilters[item.code]) {
                    const filter = {
                        ...item,
                        items: prepareItemsForFilter(item, countFilters[item.code]),
                    };
                    return newFilters.concat(filter);
                }
                return newFilters;
            },
            [],
        ),
    }),
);

/**
 * Get product badge filter from the list of available
 * @param {ApplicationStoreType} store - Application store
 * @returns {FilterDataType | undefined} product badge filter
 */
export const getProductBadgeFilterForCategory = createSelector(
    getAvailableFilters,
    // eslint-disable-next-line no-undefined
    ({items}: Array<FilterDataType>): FilterDataType | undefined =>
        items.find(({code}: FilterItemType) => code === PRODUCT_BADGE_CODE_FILTER),
);

/**
 * Is "clearance" available for the current combination of filters and categories
 * @param {ApplicationStoreType} store - Application store
 * @returns {boolean} filter clearance availability
 */
export const isClearanceAvailableForCategory = createSelector(
    getProductBadgeFilterForCategory,
    (filters: FilterDataType): boolean =>
        Boolean(filters?.items.some(({value}) => value === CLEARANCE_PRODUCT_BADGE_FILTER)),
);

/**
 * Get active category id
 * @param {ApplicationStoreType} store - Application store
 * @return {number} Active category id
 */
export const getCategoryId = (store: ApplicationStoreType): number =>
    isSearchPage(store) ? NEUTRAL_CATEGORY_ID : store.category.id;

/**
 * Get current category data
 * @param {ApplicationStoreType} store - Application store
 * @returns {CategoryType} category data
 */
export const getCategory = (store: ApplicationStoreType): CategoryType => store.category;

/**
 * Get active sorting data
 * @param {ApplicationStoreType} store - Application store
 * @return {ActiveSortType} Sorting data
 */
export const getActiveSort = (store: ApplicationStoreType): ActiveSortType => store.activeSort;

/**
 * Get is category fulfilled.
 *
 * @param {ApplicationStoreType} - Application store.
 * @returns {boolean} - Is category fulfilled.
 */
export const getIsCategoryFulfilled = isProperty('category.status', 'fulfilled');

/**
 * Get is category items fulfilled.
 *
 * @param {ApplicationStoreType} - Application store.
 * @returns {boolean} - Is category items fulfilled.
 */
export const getIsCategoryItemsFulfilled = isProperty('categoryItems.status', 'fulfilled');

/**
 * Get total category items count
 * @param {ApplicationStoreType} store - application store
 * @returns {number} items count
 */
export const getTotalItemsCount = (store: ApplicationStoreType): number =>
    store.categoryItems.total_items_count;

/**
 * Get is contacts lenses category
 *
 * @param {ApplicationStoreType} store - application store
 * @returns {boolean} - is contacts lenses category
 */
export const getIsContactLensesCategory = (store: ApplicationStoreType) =>
    store.category.is_contact_lenses || Boolean(store.routerInfo.params.isContacts);
